/**
 * Brand Studio — Project Detail (Phase 2 — Brand Engine).
 *
 * Three-section page:
 *   1) 10-question onboarding wizard (saves the brief, locks status to briefing).
 *   2) "Generate" CTA → kicks off async AI run (GPT text + Nano Banana logos / social posts).
 *      Status flips to ai_generation, then polls every 6s for assets.
 *   3) Review queue — admin sees palette / typography / tagline candidates / logo & social assets,
 *      can Approve (HUMAN GATE) or Request Revisions with feedback.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import {
  Loader2, Save, Sparkles, CheckCircle2, RotateCcw, ArrowLeft, Palette, Type,
  Image as ImageIcon, MessageSquareQuote, AlertCircle, ExternalLink, ShieldCheck,
  Download, Send, FileText, Package, Globe, Copy, Mail, MousePointerClick, Eye, Activity,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';
import BrandVersionsPanel from './BrandVersionsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRIEF_QUESTIONS = [
  { key: 'company_name', label: 'Company / brand name', placeholder: 'e.g. Aurum Capital', textarea: false },
  { key: 'sector', label: 'Sector / industry', placeholder: 'e.g. Wealth management, hospitality, SaaS', textarea: false },
  { key: 'target_audience', label: 'Target audience', placeholder: 'Who are we speaking to? Demographics, psychographics, region', textarea: true },
  { key: 'style', label: 'Visual style direction', placeholder: 'Editorial · minimal · luxurious · brutalist · playful…', textarea: true },
  { key: 'colors_preferred', label: 'Preferred colours', placeholder: 'e.g. deep navy with brass-gold accent, off-white background', textarea: false },
  { key: 'values', label: 'Core brand values', placeholder: 'Discretion, longevity, intellectual rigour…', textarea: true },
  { key: 'tone_of_voice', label: 'Tone of voice', placeholder: 'Calm and authoritative · bold and irreverent · friendly and clear', textarea: false },
  { key: 'inspirations', label: 'Brand inspirations / references', placeholder: 'Brands or designers you admire', textarea: true },
  { key: 'competitors', label: 'Competitors / direct references', placeholder: 'Who are we measured against?', textarea: true },
  { key: 'brand_goal', label: 'Primary brand goal', placeholder: 'What must this identity unlock for the business?', textarea: true },
];

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  briefing: 'bg-blue-100 text-blue-800 border-blue-200',
  ai_generation: 'bg-amber-100 text-amber-800 border-amber-200',
  bmp_review: 'bg-orange-100 text-orange-800 border-orange-200',
  revisions_client: 'bg-purple-100 text-purple-800 border-purple-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  website_prompt_sent: 'bg-cyan-100 text-cyan-800 border-cyan-200',
};

const fmtMoney = (amount, currency) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 })
    .format(Number(amount) || 0);

const assetSrc = (a) => a?.cdn_url || a?.inline_base64 || null;

const EVENT_ICON = {
  processed: <Activity className="w-3.5 h-3.5 text-gray-500" />,
  delivered: <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />,
  open: <Eye className="w-3.5 h-3.5 text-blue-600" />,
  click: <MousePointerClick className="w-3.5 h-3.5 text-[#FF6B35]" />,
  bounce: <AlertCircle className="w-3.5 h-3.5 text-red-600" />,
  dropped: <AlertCircle className="w-3.5 h-3.5 text-red-600" />,
  deferred: <Loader2 className="w-3.5 h-3.5 text-gray-500" />,
  spamreport: <AlertCircle className="w-3.5 h-3.5 text-amber-600" />,
  unsubscribe: <AlertCircle className="w-3.5 h-3.5 text-amber-600" />,
};

const EngagementTimeline = ({ events }) => {
  if (!events || events.length === 0) return null;
  const sorted = [...events].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  return (
    <div className="mt-3 border-t pt-3" data-testid="engagement-timeline">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-3.5 h-3.5 text-[#00B4D8]" />
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Engagement timeline</div>
      </div>
      <ul className="space-y-1.5">
        {sorted.map((e, i) => (
          <li key={`${e.sg_event_id || i}`} className="text-xs flex items-center gap-2 text-gray-700" data-testid={`timeline-event-${e.event}`}>
            {EVENT_ICON[e.event] || <Activity className="w-3.5 h-3.5 text-gray-400" />}
            <span className="font-medium capitalize">{e.event}</span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">{new Date(e.at).toLocaleString()}</span>
            {e.url && (
              <>
                <span className="text-gray-400">·</span>
                <a href={e.url} target="_blank" rel="noreferrer" className="text-[#00B4D8] truncate max-w-[260px] hover:underline">{e.url}</a>
              </>
            )}
            {e.reason && <span className="text-red-600 italic">({e.reason})</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const BriefForm = ({ project, headers, onSaved }) => {
  const { toast } = useToast();
  const [brief, setBrief] = useState(() => ({ ...(project.brief || {}) }));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBrief({ ...(project.brief || {}) }); }, [project.id, project.brief]);

  const update = (key, value) => setBrief((b) => ({ ...b, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/brand/projects/${project.id}/brief`, brief, { headers });
      toast({ title: 'Brief saved', description: '10 answers persisted, ready for AI generation.' });
      onSaved(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not save brief', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const filled = BRIEF_QUESTIONS.filter((q) => (brief[q.key] || '').trim().length > 0).length;

  return (
    <Card data-testid="brief-form-card">
      <CardContent className="pt-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">Onboarding Brief — 10 questions</h3>
            <p className="text-xs text-gray-500">Answers feed the master prompt of the Brand Engine. {filled}/10 filled.</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00B4D8] hover:bg-[#0096c7]" data-testid="brief-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save brief
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {BRIEF_QUESTIONS.map((q, i) => (
            <div key={q.key} className={q.textarea ? 'md:col-span-2' : ''}>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                {i + 1}. {q.label}
              </label>
              {q.textarea ? (
                <textarea
                  rows={2}
                  value={brief[q.key] || ''}
                  placeholder={q.placeholder}
                  onChange={(e) => update(q.key, e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#00B4D8]"
                  data-testid={`brief-input-${q.key}`}
                />
              ) : (
                <input
                  type="text"
                  value={brief[q.key] || ''}
                  placeholder={q.placeholder}
                  onChange={(e) => update(q.key, e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#00B4D8]"
                  data-testid={`brief-input-${q.key}`}
                />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ReviewPanel = ({ project, headers, onChanged }) => {
  const { toast } = useToast();
  const ai = project.ai_output || {};
  const palette = ai.palette || [];
  const typography = ai.typography;
  const taglines = ai.tagline_candidates || [];
  const assets = ai.assets || [];
  const [feedback, setFeedback] = useState('');
  const [approving, setApproving] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const inReview = project.status === 'bmp_review';
  const approved = project.status === 'approved' || project.status === 'delivered';

  const handleApprove = async () => {
    setApproving(true);
    try {
      const r = await axios.post(`${API_URL}/api/brand/projects/${project.id}/approve`, {}, { headers });
      toast({ title: 'Project approved', description: `Status: ${r.data.status}` });
      onChanged(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Approval failed', description: e.response?.data?.detail || e.message });
    } finally {
      setApproving(false);
    }
  };

  const handleRequestRevisions = async () => {
    if (!feedback.trim()) {
      toast({ variant: 'destructive', title: 'Feedback required', description: 'Tell the AI what to change before re-running.' });
      return;
    }
    setRequesting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/brand/projects/${project.id}/request-revisions`,
        { feedback: feedback.trim() },
        { headers }
      );
      toast({ title: 'Revisions requested', description: 'AI is re-running with your feedback.' });
      setFeedback('');
      onChanged(r.data.project || r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Revision request failed', description: e.response?.data?.detail || e.message });
    } finally {
      setRequesting(false);
    }
  };

  if (!ai.generated_at && assets.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-sm text-gray-500 py-10 text-center bg-gray-50 rounded border border-dashed">
            No AI output yet. Save the brief and trigger generation to see palette, typography and assets here.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="review-panel-card">
      <CardContent className="pt-5 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">AI Output — review queue</h3>
            <p className="text-xs text-gray-500">
              {inReview && '⚠ This project is awaiting your sign-off. Nothing is sent to the client until you approve.'}
              {approved && '✓ Project approved. Delivery phase will email final assets to the client.'}
            </p>
          </div>
          {inReview && (
            <div className="flex gap-2">
              <Button
                onClick={handleApprove}
                disabled={approving}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="approve-btn"
              >
                {approving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Approve & release gate
              </Button>
            </div>
          )}
        </div>

        {/* Tagline candidates */}
        {taglines.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquareQuote className="w-4 h-4 text-[#00B4D8]" />
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Tagline candidates</h4>
            </div>
            <ul className="space-y-1.5" data-testid="tagline-list">
              {taglines.map((t, i) => (
                <li key={i} className="text-sm bg-gray-50 border border-gray-200 rounded px-3 py-1.5">
                  <span className="text-gray-400 font-mono text-[11px] mr-2">{String(i + 1).padStart(2, '0')}</span>
                  {t}
                </li>
              ))}
            </ul>
            {ai.brand_voice && (
              <div className="mt-3 text-xs text-gray-600 italic border-l-2 border-[#00B4D8] pl-3" data-testid="brand-voice">
                {ai.brand_voice}
              </div>
            )}
          </div>
        )}

        {/* Palette */}
        {palette.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-4 h-4 text-[#00B4D8]" />
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Colour palette</h4>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-testid="palette-grid">
              {palette.map((c, i) => (
                <div key={i} className="rounded border border-gray-200 overflow-hidden">
                  <div className="h-20" style={{ backgroundColor: c.hex }} />
                  <div className="px-2 py-1.5 bg-white">
                    <div className="text-[11px] font-bold text-gray-700">{c.name}</div>
                    <div className="text-[10px] text-gray-500 font-mono">{c.hex}</div>
                    {c.role && <div className="text-[10px] text-gray-400 capitalize">{c.role}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Typography */}
        {typography && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Type className="w-4 h-4 text-[#00B4D8]" />
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Typography pairing</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="typography-block">
              <div className="border border-gray-200 rounded p-4 bg-white">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Display / headings</div>
                <div className="text-2xl font-bold text-[#0F172A] mt-1">{typography.primary}</div>
                <div className="text-xs text-gray-400 mt-1">Pair with body text in {typography.secondary}.</div>
              </div>
              <div className="border border-gray-200 rounded p-4 bg-white">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Body</div>
                <div className="text-xl font-medium text-[#0F172A] mt-1">{typography.secondary}</div>
                {typography.rationale && (
                  <div className="text-xs text-gray-500 mt-1.5 italic">{typography.rationale}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Assets */}
        {assets.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="w-4 h-4 text-[#00B4D8]" />
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Generated assets ({assets.length})</h4>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="assets-grid">
              {assets.map((a) => {
                const src = assetSrc(a);
                return (
                  <div key={a.id} className="border border-gray-200 rounded overflow-hidden bg-gray-50" data-testid={`asset-${a.kind}`}>
                    {src ? (
                      <img src={src} alt={a.label} className="w-full h-44 object-contain bg-white" loading="lazy" />
                    ) : (
                      <div className="w-full h-44 flex items-center justify-center text-xs text-gray-400">No preview</div>
                    )}
                    <div className="px-2.5 py-1.5 flex items-center justify-between">
                      <div>
                        <div className="text-[11px] font-bold text-gray-700">{a.label}</div>
                        <div className="text-[10px] text-gray-400 uppercase">{a.kind.replace(/_/g, ' ')}</div>
                      </div>
                      {a.cdn_url && (
                        <a href={a.cdn_url} target="_blank" rel="noreferrer" className="text-[#00B4D8] hover:underline">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Revisions */}
        {inReview && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-2">
              <RotateCcw className="w-4 h-4 text-amber-600" />
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Request revisions</h4>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Provide concrete feedback (e.g. "make the logo wordmark serif, warmer gold, drop the icon"). Triggers a fresh AI pass.
            </p>
            <textarea
              rows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Specific changes for the next AI iteration…"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="revisions-feedback-input"
            />
            <div className="mt-2 flex justify-end">
              <Button
                onClick={handleRequestRevisions}
                disabled={requesting || !feedback.trim()}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="request-revisions-btn"
              >
                {requesting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
                Re-run with feedback
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const WebEnginePanel = ({ project, headers, onChanged }) => {
  const { toast } = useToast();
  const we = project.web_engine || {};
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [sending, setSending] = useState(false);
  const [creds, setCreds] = useState({
    website_url: we.website_url || '',
    website_admin_email: we.website_admin_email || '',
    website_admin_password: we.website_admin_password || '',
  });
  const [promptText, setPromptText] = useState(we.master_prompt || '');

  useEffect(() => {
    setCreds({
      website_url: we.website_url || '',
      website_admin_email: we.website_admin_email || '',
      website_admin_password: we.website_admin_password || '',
    });
    setPromptText(we.master_prompt || '');
    // eslint-disable-next-line
  }, [project.id]);

  const handleGetPrompt = async () => {
    setLoadingPrompt(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/brand/projects/${project.id}/web-engine/prompt`,
        { headers, responseType: 'text' }
      );
      setPromptText(r.data);
      toast({ title: 'Master prompt ready', description: 'Copy it into Emergent Lab to spin up the website.' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not load prompt', description: e.response?.data?.detail || e.message });
    } finally {
      setLoadingPrompt(false);
    }
  };

  const handleCopyPrompt = async () => {
    if (!promptText) return;
    try {
      await navigator.clipboard.writeText(promptText);
      toast({ title: 'Prompt copied' });
    } catch {
      toast({ variant: 'destructive', title: 'Clipboard unavailable' });
    }
  };

  const handleSaveCreds = async () => {
    if (!creds.website_url.trim()) {
      toast({ variant: 'destructive', title: 'Website URL required' });
      return;
    }
    setSavingCreds(true);
    try {
      const r = await axios.put(
        `${API_URL}/api/brand/projects/${project.id}/web-engine/credentials`,
        creds,
        { headers }
      );
      toast({ title: 'Credentials saved' });
      onChanged(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not save credentials', description: e.response?.data?.detail || e.message });
    } finally {
      setSavingCreds(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(
      `Send the website URL and admin credentials to ${project.client_email}?\n\n` +
      'This releases the website handover email — make sure the URL works and credentials are correct.'
    )) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/brand/projects/${project.id}/web-engine/send`,
        {},
        { headers }
      );
      toast({ title: 'Website handover sent', description: `Email delivered to ${project.client_email}` });
      onChanged(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Send failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  const credentialsSaved = Boolean(we.website_url);
  const handoverSent = project.status === 'website_prompt_sent';

  return (
    <Card data-testid="web-engine-card">
      <CardContent className="pt-5 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#00B4D8]" />
            <h3 className="text-base font-bold text-[#0F172A]">Web Engine — prompt-only handover</h3>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Generate the master prompt for Emergent Lab, paste it there to spin up the client website,
            then save the resulting URL and admin credentials below. Sending requires the project to be in
            <code className="bg-gray-100 px-1 rounded mx-1 text-[10px]">delivered</code> status.
          </p>
        </div>

        {/* Master prompt */}
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-700">Master prompt</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleGetPrompt} disabled={loadingPrompt} data-testid="we-generate-prompt-btn">
                {loadingPrompt ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                {promptText ? 'Regenerate' : 'Generate prompt'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCopyPrompt} disabled={!promptText} data-testid="we-copy-prompt-btn">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </div>
          </div>
          <textarea
            value={promptText}
            readOnly
            rows={6}
            placeholder="Click Generate prompt to compose the Emergent Lab master prompt…"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono bg-white"
            data-testid="we-prompt-text"
          />
        </div>

        {/* Credentials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="md:col-span-3">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Website URL</label>
            <input
              type="url"
              value={creds.website_url}
              onChange={(e) => setCreds((c) => ({ ...c, website_url: e.target.value }))}
              placeholder="https://brand.emergent-lab.app"
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="we-url-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Admin email</label>
            <input
              type="email"
              value={creds.website_admin_email}
              onChange={(e) => setCreds((c) => ({ ...c, website_admin_email: e.target.value }))}
              placeholder="admin@brand.com"
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="we-admin-email-input"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Temp password</label>
            <input
              type="text"
              value={creds.website_admin_password}
              onChange={(e) => setCreds((c) => ({ ...c, website_admin_password: e.target.value }))}
              placeholder="Initial password"
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="we-admin-password-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-gray-500">
            {handoverSent ? (
              <span className="inline-flex items-center gap-1 text-cyan-700 font-semibold">
                <Mail className="w-3.5 h-3.5" /> Handover email sent
                {we.sent_at && ` · ${new Date(we.sent_at).toLocaleString()}`}
              </span>
            ) : credentialsSaved ? (
              <span className="text-gray-500">Credentials saved · ready to send.</span>
            ) : (
              <span className="text-gray-400">Save credentials, then send the handover email.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveCreds} disabled={savingCreds} variant="outline" data-testid="we-save-creds-btn">
              {savingCreds ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save credentials
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !credentialsSaved || project.status !== 'delivered'}
              className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
              data-testid="we-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Send to client
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BrandProjectDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [sendingManual, setSendingManual] = useState(false);
  const [downloadingManual, setDownloadingManual] = useState(false);
  const pollRef = useRef(null);

  const fetchProject = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/brand/projects/${id}`, { headers });
      setProject(r.data);
      return r.data;
    } catch (e) {
      if (!silent) {
        toast({ variant: 'destructive', title: 'Failed to load project', description: e.response?.data?.detail || e.message });
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchProject(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; /* eslint-disable-next-line */ }, [id]);

  // Poll every 6s while AI is running.
  useEffect(() => {
    if (project?.status === 'ai_generation') {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const fresh = await fetchProject(true);
        if (fresh && fresh.status !== 'ai_generation') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 6000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [project?.status]);

  const handleGenerate = async () => {
    setStarting(true);
    try {
      const r = await axios.post(`${API_URL}/api/brand/projects/${project.id}/generate`, {}, { headers });
      toast({ title: 'AI generation started', description: 'GPT + Nano Banana running. This typically takes 60-180 s.' });
      setProject(r.data.project || r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not start generation', description: e.response?.data?.detail || e.message });
    } finally {
      setStarting(false);
    }
  };

  const downloadBlob = async (path, suggestedFilename, kind) => {
    const setter = kind === 'pdf' ? setDownloadingPdf : setDownloadingZip;
    setter(true);
    try {
      const r = await axios.get(`${API_URL}${path}`, { headers, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = suggestedFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast({ title: `${kind === 'pdf' ? 'Brand book PDF' : 'Delivery ZIP'} downloaded` });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast({ variant: 'destructive', title: `Could not download ${kind}`, description: detail });
    } finally {
      setter(false);
    }
  };

  const handleDeliver = async () => {
    if (!window.confirm(
      `Send the brand package to ${project.client_email || 'the client'}?\n\n` +
      'A SendGrid email with the full ZIP (brand book PDF + logos + social kit + mockups) will be dispatched. ' +
      'This action releases the deliverable — make sure you have approved the project first.'
    )) return;
    setDelivering(true);
    try {
      const r = await axios.post(`${API_URL}/api/brand/projects/${project.id}/deliver`, {}, { headers });
      toast({
        title: 'Delivery dispatched',
        description: `${r.data.filename} (${Math.round((r.data.zip_size_bytes || 0) / 1024)} KB) — sent to ${r.data.email?.to}`,
      });
      await fetchProject();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delivery failed', description: e.response?.data?.detail || e.message });
    } finally {
      setDelivering(false);
    }
  };

  const handleDownloadHostingManual = async () => {
    setDownloadingManual(true);
    try {
      const r = await axios.get(`${API_URL}/api/brand/hosting-manual/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.project_code || 'BRD'}-hosting-manual.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Hosting manual downloaded' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Download failed', description: e.response?.data?.detail || e.message });
    } finally {
      setDownloadingManual(false);
    }
  };

  const handleSendHostingManual = async () => {
    if (!project.client_email) {
      toast({ variant: 'destructive', title: 'No client email', description: 'Set a client email before sending.' });
      return;
    }
    if (!window.confirm(`Send the hosting manual PDF to ${project.client_email}?`)) return;
    setSendingManual(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/brand/projects/${project.id}/hosting-manual/send`,
        {},
        { headers }
      );
      toast({ title: 'Hosting manual sent', description: `Email delivered to ${r.data.to}` });
      await fetchProject();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Send failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSendingManual(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#00B4D8]" /></div>;
  }
  if (!project) return null;

  const briefFilled = Object.values(project.brief || {}).filter((v) => typeof v === 'string' && v.trim().length > 0).length;
  const canGenerate = briefFilled >= 5 && ['briefing', 'draft', 'bmp_review', 'revisions_client', 'approved'].includes(project.status);
  const isGenerating = project.status === 'ai_generation';

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="brand-project-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/brand-studio" className="text-sm text-gray-500 hover:text-[#00B4D8] inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Brand Studio
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {project.pending_review && (
            <Badge className="bg-[#FF6B35] text-white inline-flex items-center gap-1" data-testid="pending-review-badge">
              <ShieldCheck className="w-3 h-3" /> Awaiting BMP sign-off
            </Badge>
          )}
          <Badge variant="outline" className={STATUS_BADGE[project.status] || STATUS_BADGE.draft}>
            {(project.status || '').replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Code</div>
              <div className="font-mono font-bold text-[#0F172A]">{project.project_code}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Client</div>
              <div className="font-medium text-[#0F172A]">{project.client_name || '—'}</div>
              <div className="text-xs text-gray-500">{project.client_email || ''}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Plan</div>
              <div className="font-medium text-[#0F172A]">{project.plan_snapshot?.name || project.plan_slug}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Price</div>
              <div className="font-bold text-[#0F172A]">{fmtMoney(project.price_amount, project.currency)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <BriefForm project={project} headers={headers} onSaved={setProject} />

      {/* AI generation trigger */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FF6B35]" />
                <h3 className="text-base font-bold text-[#0F172A]">Brand Engine — AI generation</h3>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {isGenerating
                  ? 'Generation in progress — palette, typography, 3 logo variants and 3 social posts. Polling every 6 s…'
                  : 'Trigger GPT (palette + typography + tagline + voice) and Nano Banana (3 logos + 3 social posts).'}
              </p>
              {!canGenerate && !isGenerating && (
                <p className="text-xs text-amber-600 mt-1 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Fill at least 5 brief questions to unlock generation.
                </p>
              )}
            </div>
            <Button
              onClick={handleGenerate}
              disabled={!canGenerate || starting || isGenerating}
              className="bg-[#FF6B35] hover:bg-[#e85a25] text-white"
              data-testid="generate-btn"
            >
              {starting || isGenerating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {isGenerating ? 'Generating…' : project.ai_output?.assets?.length ? 'Re-generate' : 'Generate brand'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ReviewPanel project={project} headers={headers} onChanged={setProject} />

      {/* Version history + Lab integration */}
      <BrandVersionsPanel project={project} headers={headers} onChanged={fetchProject} />

      {/* Phase 3 — Delivery (PDF / ZIP / SendGrid) */}
      {(project.ai_output?.assets?.length || 0) > 0 && (
        <Card data-testid="delivery-panel-card">
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#FF6B35]" />
                  <h3 className="text-base font-bold text-[#0F172A]">Delivery</h3>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Brand Book PDF · 3 logos · social kit · mockups · README — packaged into a ZIP and emailed to the client (gated by approval).
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => downloadBlob(`/api/brand/projects/${project.id}/brand-book.pdf`, `${project.project_code || 'BRD'}-brand-book.pdf`, 'pdf')}
                disabled={downloadingPdf}
                className="border-gray-300 text-[#0F172A]"
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
                Preview Brand Book PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => downloadBlob(`/api/brand/projects/${project.id}/delivery.zip`, `${project.project_code || 'BRD'}-package.zip`, 'zip')}
                disabled={downloadingZip}
                className="border-gray-300 text-[#0F172A]"
                data-testid="download-zip-btn"
              >
                {downloadingZip ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                Download Delivery ZIP
              </Button>
              <Button
                onClick={handleDeliver}
                disabled={delivering || project.status !== 'approved' || !project.client_email}
                className="bg-[#FF6B35] hover:bg-[#e85a25] text-white"
                data-testid="send-delivery-btn"
              >
                {delivering ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Send to client
              </Button>
            </div>

            {project.status === 'delivered' && project.delivered_at && (
              <div className="mt-2 text-xs bg-green-50 border border-green-200 rounded px-3 py-2 text-green-800 inline-flex items-center gap-2" data-testid="delivered-banner">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Delivered on {new Date(project.delivered_at).toLocaleString()} · {Math.round((project.delivery_size_bytes || 0) / 1024)} KB · sent to {project.delivery_email?.to}
              </div>
            )}
            {project.status !== 'approved' && project.status !== 'delivered' && (
              <div className="mt-2 text-xs text-amber-700 inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Approve the project (above) before the "Send to client" button is enabled.
              </div>
            )}
            {!project.client_email && (
              <div className="mt-2 text-xs text-red-600 inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Project has no client email — cannot dispatch delivery.
              </div>
            )}

            <EngagementTimeline events={project.delivery_events} />

            {/* Hosting Manual handout — separate from main delivery */}
            <div className="mt-4 pt-4 border-t border-gray-200" data-testid="hosting-manual-block">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div>
                  <div className="text-sm font-semibold text-[#0F172A]">Hosting Manual</div>
                  <p className="text-xs text-gray-500">
                    Explains to the client how the website is hosted, monthly fees, and the domain disclaimer.{' '}
                    <Link to="/admin/brand-studio/hosting-manual" className="text-[#00B4D8] hover:underline">
                      Edit content
                    </Link>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={handleDownloadHostingManual}
                  disabled={downloadingManual}
                  className="border-gray-300 text-[#0F172A]"
                  data-testid="download-hosting-manual-btn"
                >
                  {downloadingManual ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
                  Download Hosting Manual PDF
                </Button>
                <Button
                  onClick={handleSendHostingManual}
                  disabled={sendingManual || !project.client_email}
                  className="bg-[#0F172A] hover:bg-[#1e293b] text-white"
                  data-testid="send-hosting-manual-btn"
                >
                  {sendingManual ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                  Send Manual to client
                </Button>
              </div>
              {project.hosting_manual_sent_at && (
                <div className="mt-2 text-xs bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-blue-800 inline-flex items-center gap-1.5" data-testid="hosting-manual-sent-banner">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Manual sent on {new Date(project.hosting_manual_sent_at).toLocaleString()}
                  {project.hosting_manual_sent_to ? ` to ${project.hosting_manual_sent_to}` : ''}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 4 — Web Engine (Prompt-only handover to Emergent Lab) */}
      {(project.status === 'delivered' || project.status === 'website_prompt_sent') && (
        <WebEnginePanel project={project} headers={headers} onChanged={setProject} />
      )}
    </div>
  );
};

export default BrandProjectDetail;
